import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  IconButton,
  DrawerHeader,
  DrawerTitle,
  DrawerBody
} from '@one-thd/sui-atomic-components';
import { ArrowBack, Close } from '@one-thd/sui-icons';
import { GiftCardBarcodeCarousel } from '../scan-to-pay/gift-card-barcode-carousel';
import { GiftCardBarcode } from '../scan-to-pay/gift-card-barcode';
import { LoadingSpinner } from '../loading-spinner';
import { OTP_AUTH_ERROR } from '../otp/otp-text-constants';
import { IErrorProps } from '../../models/common-models';
import { useListContext, LIST_ACTIONS } from '../../context/transactable-gift-cards-context';
import { useGiftCardBarcodeService } from '../../services/gift-card-barcode-service';

interface IGiftCardBarcodeList {
  otpVerificationInput?: {
    email: string;
    encryptedPayload: string;
    otp: string;
  };
  onClose: () => void;
  onBack?: () => void;
  onOTPVerified?: (verified: boolean) => void;
}

const GiftCardBarcodeList: FC<IGiftCardBarcodeList> = ({
  otpVerificationInput,
  onClose,
  onBack,
  onOTPVerified
}) => {
  const { getGiftCardBarcodes } = useGiftCardBarcodeService();
  const [isGetBarcodeLoading, setIsGetBarcodeLoading] = useState(false);
  const [error, setError] = useState<IErrorProps | null>(null);
  const { state, dispatch } = useListContext();
  const { selectedGiftCardPaymentIDs, transactableGiftCardList } = state;
  const selectedCards = transactableGiftCardList.filter((card) => selectedGiftCardPaymentIDs?.includes(card.id)
  );

  const handleOnBack = () => {
    dispatch({ type: LIST_ACTIONS.SET_LIST, payload: [] });
    if (onBack) {
      onBack();
    }
  };

  const handleGetCards = async () => {
    setIsGetBarcodeLoading(true);
    setError(null);

    try {
      const result = await getGiftCardBarcodes(selectedGiftCardPaymentIDs || [], otpVerificationInput);

      if (result.success && result.barcodeData) {
        dispatch({ type: 'UPDATE_CARD_BARCODE', payload: result.barcodeData });

        // If we successfully get gift card data, mark OTP as verified
        if (result.barcodeData.length > 0 && onOTPVerified) {
          onOTPVerified(true);
        }
      } else {
        setError({ errorMessage: result.error || 'Failed to retrieve gift card data', errorCode: 'BARCODE_ERROR' });
      }
    } catch (err) {
      setError({ errorMessage: 'Failed to retrieve gift card data', errorCode: 'NETWORK_ERROR' });
    } finally {
      setIsGetBarcodeLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCards?.length > 0) {
      // Check if all selected cards already have barcode data
      // This happens when OTP verification successfully fetched the data
      const hasAllBarcodeData = selectedCards.every((card) => card.barcode && card.pin);

      // Only call API if cards don't have barcode data yet
      if (!hasAllBarcodeData) {
        handleGetCards();
      }
    }
  }, []);

  const renderBarcodeList = () => {
    return selectedCards.length === 1 ? (
      <GiftCardBarcode giftCard={selectedCards[0]} onClose={onClose} />
    ) : (
      <GiftCardBarcodeCarousel giftCardsList={selectedCards} onClose={onClose} />
    );
  };

  const renderBody = () => {
    if (selectedCards.length === 0) {
      return null;
    }
    if (isGetBarcodeLoading) {
      return <LoadingSpinner />;
    }
    if (error?.errorMessage) {
      return (
        <Alert size="large" status="error">
          {error.errorMessage}
        </Alert>
      );
    }
    return renderBarcodeList();
  };

  const renderHeader = () => {
    const isOTPTimeout = OTP_AUTH_ERROR.includes(error?.errorCode || '');

    if (onBack && !isOTPTimeout) {
      return (
        <DrawerHeader
          startAdornment={(
            <Button
              disabled={isGetBarcodeLoading}
              aria-label="back"
              variant="ghost"
              startIcon={ArrowBack}
              onClick={handleOnBack}
              data-testid="back-button"
            >
              Back
            </Button>
          )}
          endAdornment={<IconButton aria-label="close" onClick={onClose} icon={Close} />}
        >
          <DrawerTitle>Scan Gift Card</DrawerTitle>
        </DrawerHeader>
      );
    }

    return <DrawerHeader title="Scan Gift Cards" onClose={onClose} />;
  };

  return (
    <>
      {renderHeader()}
      <DrawerBody>{renderBody()}</DrawerBody>
    </>
  );
};

export { GiftCardBarcodeList };
